import React, { useState } from 'react';
import { Box, Button, Typography, Divider, CircularProgress } from '@mui/material';
import { Grid } from '@material-ui/core';
import DeleteIcon from '../SvgIcons/DeleteIcon';
import Axios from '../../../config/axios';
import { UrlConstant } from '../../../commom/UrlConstant';
import Successpopup from '../SharedComponent/Successpopup';
import Confirmpopup from '../SharedComponent/Confirmpopup';
import CardIcon from '../SvgIcons/AddCardIcon';
import BankIcon from '../SvgIcons/BankIcon';
import AuthorizeNetIframe from '../SharedComponent/AuthorizeNetIframe';
import { renderPaymentIcon } from '../../../utils/utils';
import useAeroPayBankLink from '../../../hooks/useaeropaybanklink';
import AeroSyncWidgetModal from '../SharedComponent/Aerosyncwidgetmodal';
import OtpVerificationDialog from '../Dialogs/Otpverificationdialog';
import { useNavigate } from 'react-router-dom';


const Repayment: React.FC<any> = ({
    paymentMethods,
    setSelectedPaymentMethod,
    handlepayment,
    fetchPaymentMethods,
    canAddBank
}) => {
    const handleChangeMethod = (method) => {
        setSelectedPaymentMethod(method);
        handlepayment?.();
    };

    // Authorize.Net (Card) states
    const [loadingType, setLoadingType] = useState<string | null>(null);
    const [iframeOpen, setIframeOpen] = useState(false);
    const [iframeToken, setIframeToken] = useState('');
    const [formAction, setFormAction] = useState('');

    // Popup states
    const [popupOpen, setPopupOpen] = useState(false);
    const [popupSuccess, setPopupSuccess] = useState(true);
    const [popupHeading, setPopupHeading] = useState('');
    const [popupMessage, setPopupMessage] = useState('');
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<any>(null);
    const navigate = useNavigate();

    // AeroPay Bank Link Hook
    const {
        isWidgetOpen,
        isLinking,
        isLoading: isBankLoading,
        needsOtpVerification,
        otp,
        isVerifyingOtp,
        widgetUrl,
        error: bankError,
        startBankLink,
        handleOtpVerification,
        cancelOtpVerification,
        closeWidget,
        setOtp,
    } = useAeroPayBankLink({
        onSuccess: (connectionId) => {
            setPopupSuccess(true);
            setPopupHeading('Bank Account Linked');
            setPopupMessage('Your bank account was linked successfully.');
            setPopupOpen(true);
            if (fetchPaymentMethods) fetchPaymentMethods();
        },
        onError: (error) => {
            setPopupSuccess(false);
            setPopupHeading('Link Failed');
            setPopupMessage(error);
            setPopupOpen(true);
        },
    });

    /**
     * Add payment profile for Authorize.Net (Card)
     */
    const addPaymentProfile = async (type: 'Card') => {
        try {
            const response = await Axios.get(UrlConstant.BoosterLoan.BOOSTER_LOAN_ADD_PAYMENT_PROFILE, {
                params: { type },
            });
            return response.data;
        } catch (error: any) {
            console.log(error);
            throw error?.response?.data?.message || error.message || 'Something went wrong';
        }
    };

    /**
     * Handle adding a payment method
     */
    const handleAddPayment = async (type: 'Card' | 'Bank') => {
        if (type === 'Bank') {
            // Use AeroPay for bank accounts
            startBankLink();
        } else {
            // Use Authorize.Net for cards
            setLoadingType(type);
            try {
                const res = await addPaymentProfile(type);
                setIframeToken(res?.data?.token);
                setFormAction('https://test.authorize.net/customer/addPayment');
                setIframeOpen(true);
            } catch (err: any) {
                setPopupSuccess(false);
                setPopupHeading('Add Payment Failed');
                setPopupMessage(err);
                setPopupOpen(true);
            } finally {
                setLoadingType(null);
            }
        }
    };

    /**
     * Show confirm dialog before actual delete
     */
    const handleDeleteClick = (item: any) => {
        setItemToDelete(item);
        setConfirmOpen(true);
    };

    /**
     * Actual delete logic after confirmation
     */
    const handleDeleteConfirmed = async () => {
        if (!itemToDelete) return;
        setLoadingType('delete-' + (itemToDelete?.customerPaymentProfileId || ''));
        setConfirmOpen(false);
        try {
            const response = await Axios.delete(UrlConstant.BoosterLoan.BOOSTER_LOAN_DELETE_PAYMENT_PROFILE, {
                params: { customerPaymentProfileId: itemToDelete?.customerPaymentProfileId },
            });
            setPopupSuccess(true);
            setPopupHeading('Delete Successful');
            setPopupMessage(response.data?.message || 'Profile deleted');
            setPopupOpen(true);
            if (fetchPaymentMethods) fetchPaymentMethods();
        } catch (error: any) {
            setPopupSuccess(false);
            setPopupHeading('Delete Failed');
            setPopupMessage(error?.response?.data?.message || error.message || 'Something went wrong');
            setPopupOpen(true);
        } finally {
            setLoadingType(null);
            setItemToDelete(null);
        }
    };

    const handleDialogClose = () => {
        setIframeOpen(false);
    };

    // Show error from bank link in popup
    React.useEffect(() => {
        if (bankError) {
            setPopupSuccess(false);
            setPopupHeading('Bank Link Error');
            setPopupMessage(bankError);
            setPopupOpen(true);
        }
    }, [bankError]);

    return (
        <Box sx={{ margin: '0 auto', height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Success/Error Popup */}
            {popupOpen && (
                <Successpopup
                    onClose={() => setPopupOpen(false)}
                    Success={popupSuccess}
                    Heading={popupHeading}
                    Message={popupMessage}
                />
            )}

            {/* Payment Methods List */}
            {!iframeOpen && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, m: 2 }}>
                    {paymentMethods.map((method) => (
                        <Box
                            key={method.id}
                            className="dcard-hover"
                            onClick={() => handleChangeMethod(method)}
                            sx={{
                                borderRadius: '12px',
                                border: '1px solid rgba(255,255,255,0.08)',
                                backgroundColor: 'rgba(255,255,255,0.02)',
                                transition: 'all 0.3s ease',
                                cursor: 'pointer',
                                padding: '16px 24px',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 4px 12px rgba(74, 122, 242, 0.15)',
                                },
                                margin: '0 10px',
                            }}
                        >
                            <Grid container alignItems="center" justifyContent="space-between">
                                <Grid item xs={1}>
                                    {renderPaymentIcon(method, 0)}
                                </Grid>
                                <Grid item xs={10}>
                                    <Box sx={{ ml: '20px' }}>
                                        <Typography
                                            variant="h6"
                                            className="text-white"
                                            sx={{ fontWeight: 500, fontSize: '16px', mb: 0.5 }}
                                        >
                                            {method.type === 'shareSellDown'
                                                ? method.displayName
                                                : `${method.displayName} ${method.displayNumber}`}
                                        </Typography>
                                        {method.metadata?.description && (
                                            <Typography
                                                sx={{
                                                    fontSize: '13px',
                                                    color: 'rgba(255, 255, 255, 0.6)',
                                                    lineHeight: 1.4,
                                                }}
                                            >
                                                {method.metadata.description}
                                            </Typography>
                                        )}
                                    </Box>
                                </Grid>
                                <Grid item xs={1} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                    {/* Optionally add delete/edit buttons here */}
                                </Grid>
                            </Grid>
                        </Box>
                    ))}
                </Box>
            )}

            {/* Authorize.Net Card Flow */}
            <AuthorizeNetIframe
                token={iframeToken}
                open={iframeOpen}
                onClose={handleDialogClose}
                formAction={formAction}
                title="Add Payment Card"
                paymentType="Card"
                onSuccess={() => {
                    setPopupSuccess(true);
                    setPopupHeading('Payment Method Added');
                    setPopupMessage('Your payment method was added successfully.');
                    setPopupOpen(true);
                    setIframeOpen(false);
                    if (fetchPaymentMethods) fetchPaymentMethods();
                }}
                onCancel={() => {
                    setIframeOpen(false);
                }}
            />

            {/* AeroPay OTP Verification Dialog */}
            <OtpVerificationDialog
                open={needsOtpVerification}
                otp={otp}
                isVerifying={isVerifyingOtp}
                onOtpChange={setOtp}
                onVerify={handleOtpVerification}
                onCancel={cancelOtpVerification}
                title="Verify Your Account"
                description="A 6-digit OTP has been sent to your registered email/phone. Please enter it below to continue."
            />

            {/* AeroPay Widget Modal */}
            <AeroSyncWidgetModal
                open={isWidgetOpen}
                widgetUrl={widgetUrl}
                isLinking={isLinking}
                onClose={closeWidget}
                loadingMessage="Linking your bank account..."
                title="Connect Bank Account"
            />

            {/* Confirm Delete Popup */}
            <Confirmpopup
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                onConfirm={handleDeleteConfirmed}
                message={'Are you sure you want to delete this payment method?'}
            />

            {/* Footer Section with Buttons */}
            <Box sx={{ mt: 'auto', pt: 3 }}>
                <Divider sx={{ borderColor: 'rgba(255,255,255,0.12)', mb: 2 }} />
                <Box display="flex" gap={2} width="100%" justifyContent="center" pb={2}>
                    {/* <Button
                        variant="outlined"
                        startIcon={<CardIcon />}
                        onClick={() => handleAddPayment('Card')}
                        disabled={loadingType === 'Card'}
                        sx={{
                            minWidth: 160,
                            borderRadius: '28px',
                            fontWeight: 500,
                            fontSize: '16px',
                            borderColor: '#4edf6d',
                            color: '#4edf6d',
                            textTransform: 'none',
                            px: 2,
                            py: 1.5,
                            '&:hover': {
                                borderColor: '#3dc95a',
                                color: '#3dc95a',
                                bgcolor: 'rgba(78, 223, 109, 0.1)',
                            },
                            '&.Mui-disabled': {
                                borderColor: 'rgba(255,255,255,0.12)',
                                color: 'rgba(255,255,255,0.5)',
                            },
                        }}
                    >
                        {loadingType === 'Card' ? <CircularProgress size={20} /> : 'Add Card'}
                    </Button> */}
                    {/* {canAddBank && (
                        <Button
                            variant="contained"
                            startIcon={<BankIcon />}
                            onClick={() => navigate('/linkbankaccount')}
                            disabled={isBankLoading}
                            sx={{
                                minWidth: 160,
                                borderRadius: '28px',
                                fontWeight: 500,
                                fontSize: '16px',
                                background: 'linear-gradient(90deg, #f2674a 0%, #ffbf00 100%)',
                                color: '#fff',
                                textTransform: 'none',
                                px: 2,
                                py: 1.5,
                                '&:hover': {
                                    background: 'linear-gradient(90deg, #d55a3f 0%, #e6ac00 100%)',
                                },
                                '&.Mui-disabled': {
                                    background: 'rgba(255,255,255,0.12)',
                                    color: 'rgba(255,255,255,0.5)',
                                },
                            }}
                        >
                            {isBankLoading ? <CircularProgress size={20} color="primary" /> : 'Add Bank'}
                        </Button>
                    )} */}
                </Box>
            </Box>
        </Box>
    );
};

export default Repayment;